<template>
  <div class="workspace-document-status-dropdown">
    <vf-button
      data-testid="workspace-document-status-action"
      :busy="isBusy"
      :size="ButtonSize.MEDIUM"
      :variant="ButtonVariant.SECONDARY"
      @click="updateStatus"
    >
      {{ t(actionConfig.labelKey) }}
    </vf-button>
  </div>
</template>

<script setup lang="ts">
  import { useNavigator } from '@/composables/useNavigator';
  import { useToast } from '@/composables/useToast';
  import { endpointsList } from '@/helpers';
  import VfButton from '@/components/VfButton/VfButton.vue';
  import {
    ButtonSize,
    ButtonVariant,
  } from '@/components/VfButton/VfButton.types';
  import { WorkspaceDetailsTab } from '@/views/WorkspaceDetails/WorkspaceDetails.types';
  import axios from 'axios';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { WorkspaceDocumentStatusDropdownProps } from './WorkspaceDocumentStatusDropdown.types';

  const props = defineProps<WorkspaceDocumentStatusDropdownProps>();

  const emit = defineEmits<{
    (e: 'dropdown-update'): void;
  }>();

  const { t } = useI18n();
  const { createErrorToast, createSuccessToast } = useToast();
  const { goToWorkspaceDetailsTab } = useNavigator();
  const isBusy = ref(false);

  const actionConfig = computed(() => {
    if (props.action === 'setInReview') {
      return {
        labelKey: 'workspaceDocumentStatusIndicator.actions.setInReview',
        successToastKey: 'workspaceDocumentStatusIndicator.success.setInReview',
        errorToastKey:
          'workspaceDocumentStatusIndicator.errors.cannotSetInReview',
        request: () =>
          axios.patch(
            endpointsList.workspaceUploads.setWorkspaceDocumentInReview(
              props.workspaceId,
              props.documentId,
            ),
          ),
      };
    }

    return {
      labelKey: 'workspaceDocumentStatusIndicator.actions.approve',
      successToastKey: 'workspaceDocumentStatusIndicator.success.approve',
      errorToastKey: 'workspaceDocumentStatusIndicator.errors.cannotApprove',
      request: () =>
        axios.post(
          endpointsList.workspaceUploads.applyWorkspaceDocumentWorkingCopy(
            props.workspaceId,
            props.documentId,
          ),
        ),
    };
  });

  async function updateStatus(): Promise<void> {
    try {
      isBusy.value = true;
      await actionConfig.value.request();
      createSuccessToast(t(actionConfig.value.successToastKey));
      if (props.action === 'approve') {
        goToWorkspaceDetailsTab(
          props.workspaceId,
          WorkspaceDetailsTab.DOCUMENTS,
        );
        return;
      }
      emit('dropdown-update');
    } catch {
      createErrorToast(t(actionConfig.value.errorToastKey));
    } finally {
      isBusy.value = false;
    }
  }
</script>

<style scoped lang="scss">
  .workspace-document-status-dropdown {
    padding: 1rem 0.5rem;
    min-width: 240px;
    display: flex;
    justify-content: center;
  }
</style>
